import React from 'react';
import FeaturePageTemplate from '../components/FeaturePageTemplate';

const AIPlatformPage = () => {
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  const benefits = [
    "Intelligent anomaly detection that adapts to your data patterns",
    "Data quality rule recommendations to enhance monitoring coverage",
    "Simplified interaction with natural language assistance",
    "Accelerated root cause analysis for faster problem resolution",
    "Continuous learning that improves over time with your data"
  ];

  const details = [
    {
      title: "Anomaly Detection System",
      description: "Qupid leverages advanced machine learning to detect anomalies in your data, making your quality checks more intelligent and adaptable.",
      items: [
        "Automatic pattern recognition across historical data",
        "Dynamic thresholds that adjust to seasonal and trend patterns",
        "Multi-dimensional anomaly detection across related metrics",
        "Unsupervised learning for new data sources",
        "Confidence scoring and explanation for detected anomalies"
      ]
    },
    {
      title: "Recommendations and Suggestions",
      description: "Based on your data and metadata, Qupid proactively suggests quality rules and monitoring configurations to enhance coverage.",
      items: [
        "Data quality rule suggestions based on observed patterns",
        "Column-specific quality checks recommended from metadata",
        "Configuration optimization for existing monitoring",
        "Coverage gap identification across your data ecosystem",
        "Prioritized suggestions based on data criticality"
      ]
    },
    {
      title: "Qupid Assistant",
      description: "A natural language interface that simplifies interaction with Qupid, enhancing productivity and reducing the learning curve.",
      items: [
        "Natural language queries for data quality status",
        "Conversational interface for creating and modifying rules",
        "Interactive troubleshooting assistance",
        "Context-aware help and documentation",
        "Explanation of quality issues in plain language"
      ]
    },
    {
      title: "Root Cause Analysis Algorithm",
      description: "Qupid's AI accelerates incident resolution with intelligent root cause analysis that quickly identifies the source of problems.",
      items: [
        "Automatic correlation of quality incidents across systems",
        "Pattern recognition for common failure modes",
        "Timeline analysis with contextual event integration",
        "Impact prediction based on lineage information",
        "Resolution suggestion based on historical incidents"
      ]
    }
  ];

  const useCases = [
    {
      title: "Intelligent Data Quality Monitoring",
      description: "Move beyond static thresholds with machine learning-powered anomaly detection that adapts to your data's natural patterns and seasonality."
    },
    {
      title: "Accelerated Quality Coverage",
      description: "Rapidly implement comprehensive data quality monitoring with AI-suggested rules based on your data's characteristics and structure."
    },
    {
      title: "Simplified Data Operations",
      description: "Enable data team members of all technical levels to interact with quality monitoring through the natural language Qupid Assistant."
    },
    {
      title: "Rapid Incident Resolution",
      description: "Cut mean time to repair for data incidents with AI-powered root cause analysis that quickly identifies the origin of quality issues."
    }
  ];

  return (
    <FeaturePageTemplate
      title="AI-Powered Platform"
      description="Qupid's AI capabilities enhance every aspect of data quality management, from intelligent anomaly detection to smart recommendations, natural language assistance, and accelerated root cause analysis."
      icon={icon}
      benefits={benefits}
      details={details}
      useCases={useCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">AI Technology Stack</h3>
          <p className="mb-4">
            Qupid's AI engine is built on a modern machine learning architecture combining time-series analysis, natural language processing, and graph analytics to provide comprehensive intelligence across data quality domains.
          </p>
          <p>
            Our models continuously learn from your data patterns and user interactions to improve accuracy and relevance over time, while maintaining strict privacy boundaries. The platform supports explainable AI principles, providing clear reasoning for all suggestions and detections to build trust in automated decisions.
          </p>
        </div>
      }
    />
  );
};

export default AIPlatformPage; 